import styled, { css } from 'styled-components';

import { FesLink } from '../../core/link/FesLink';
import { media } from '../../core/styles';

// Shared by linked categories and categories without a link (rendered as plain text).
const categoryItemStyles = css<{ $first: boolean }>`
  ${({ theme, $first }) => css`
    align-items: center;
    color: ${theme.colors.content.component.header.color};
    display: flex;
    font-size: ${theme.font.size[3]};
    padding: 0 ${theme.padding['2xs']};
    margin-left: ${$first ? theme.spacing.x.md : 0};
    text-decoration: none;
    text-transform: uppercase;
  `}
`;

export const S = {
  Navigation: styled.nav`
    display: none;
    height: var(--top-nav-height);

    ${({ theme }) => css`
      @media ${media(theme).greaterThan('lg')} {
        display: block;
      }
    `}
  `,

  CategoryList: styled.ul`
    display: flex;
    height: 100%;
    list-style: none;
    margin: 0;
    padding: 0;
  `,

  Category: styled.li`
    align-items: stretch;
    display: flex;
    height: 100%;
  `,

  CategoryLink: styled(FesLink)<{ $first: boolean }>`
    ${categoryItemStyles}

    ${({ theme }) => css`
      &:focus-visible {
        outline: 2px solid ${theme.colors.content.focus};
        outline-offset: -2px;
      }
    `}
  `,

  CategoryText: styled.span<{ $first: boolean }>`
    ${categoryItemStyles}

    cursor: default;
  `,

  CategoryLabel: styled.span<{ $active: boolean; $navigationOpen: boolean }>`
    ${({ theme, $active, $navigationOpen }) => css`
      box-sizing: border-box;
      border-bottom: ${theme.borders.width.xs} solid
        ${$active ? theme.colors.content.component.header.color : 'transparent'};
      font-weight: ${$active || !$navigationOpen
        ? theme.variables.font.weight.medium
        : theme.variables.font.weight.regular};
      line-height: 24px;
    `}
  `,

  Toggle: styled.button`
    ${({ theme }) => css`
      align-items: center;
      align-self: stretch;
      background: transparent;
      border: 0;
      cursor: pointer;
      display: flex;
      justify-content: center;
      margin-inline: -${theme.padding['2xs']};
      opacity: 0;
      width: ${theme.size[4]};

      &::after {
        border-bottom: ${theme.borders.width.xs} solid
          ${theme.colors.content.component.header.color};
        border-right: ${theme.borders.width.xs} solid ${theme.colors.content.component.header.color};
        content: '';
        height: 6px;
        transform: translateY(-2px) rotate(45deg);
        transition: transform 150ms ease-in-out;
        width: 6px;

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }

      &[aria-expanded='true']::after {
        transform: translateY(2px) rotate(225deg);
      }

      &:focus-visible {
        opacity: 1;
        outline: 2px solid ${theme.colors.content.focus};
        outline-offset: -2px;
      }
    `}
  `,

  Backdrop: styled.div<{ $open: boolean }>`
    display: none;

    ${({ theme, $open }) => css`
      @media ${media(theme).coarsePointer} {
        background: rgb(33 37 41 / 35%);
        display: block;
        inset: var(--top-nav-height) 0 0;
        opacity: ${$open ? 1 : 0};
        pointer-events: ${$open ? 'auto' : 'none'};
        position: fixed;
        transition: opacity 120ms ease-out, visibility 120ms ease-out;
        visibility: ${$open ? 'visible' : 'hidden'};
        z-index: 0;

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }
    `}
  `,

  Panel: styled.div<{ $open: boolean }>`
    ${({ theme, $open }) => css`
      background: ${theme.colors.background.base};
      border-bottom: 1px solid ${theme.colors.border.subtle};
      box-shadow: 0 4px 16px rgb(0 0 0 / 8%);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      left: 0;
      max-height: calc(100vh - var(--top-nav-height));
      min-height: 480px;
      opacity: ${$open ? 1 : 0};
      overflow-y: auto;
      pointer-events: ${$open ? 'auto' : 'none'};
      position: fixed;
      right: 0;
      top: var(--top-nav-height);
      transition: opacity ${$open ? '160ms ease-in' : '120ms ease-out'},
        visibility ${$open ? '0ms' : '120ms'};
      visibility: ${$open ? 'visible' : 'hidden'};
      z-index: 1;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `}
  `,

  Columns: styled.div`
    ${({ theme }) => css`
      box-sizing: border-box;
      display: flex;
      flex: 1 0 auto;
      gap: ${theme.size[8]};
      justify-content: space-between;
      margin: 0 auto;
      padding: ${theme.padding.md} ${theme.padding.md} ${theme.padding.lg};
      width: 100%;
    `}
  `,

  Column: styled.section`
    flex: 1 1 0;
    min-width: 130px;
  `,

  Heading: styled.h2<{ $visuallyHidden?: boolean }>`
    ${({ theme, $visuallyHidden }) => css`
      color: ${theme.colors.content.primary};
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.medium};
      letter-spacing: 0.5px;
      line-height: 20px;
      margin: 0 0 ${theme.padding.sm};
      text-transform: uppercase;

      ${$visuallyHidden &&
      css`
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        white-space: nowrap;
        width: 1px;
      `}
    `}
  `,

  HeadingText: styled.span`
    ${({ theme }) => css`
      color: ${theme.colors.content.primary};
      cursor: default;
      user-select: none;
    `}
  `,

  HeadingLink: styled(FesLink)`
    ${({ theme }) => css`
      color: ${theme.colors.content.primary};
    `}
  `,

  Groups: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.size[6]};
  `,

  LinkList: styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    gap: ${({ theme }) => theme.size[2]};
    display: flex;
    flex-direction: column;
  `,

  LinkItem: styled.li`
    ${({ theme }) => css`
      color: ${theme.colors.content.secondary};
      font-size: ${theme.font.size[3]};
      line-height: 1.4;
    `}
  `,

  Link: styled(FesLink)<{ $color?: string; $fontWeight?: number }>`
    ${({ theme, $color, $fontWeight }) => css`
      align-items: baseline;
      color: ${$color ?? theme.colors.content.secondary};
      display: inline-flex;
      font-weight: ${$fontWeight ?? 'inherit'};
      gap: 6px;
      white-space: normal;
      word-break: break-word;

      &:hover,
      &:focus-visible {
        color: ${theme.colors.content.primary};
      }
    `}
  `,

  ExternalIcon: styled.span`
    display: inline-flex;
    flex-shrink: 0;
    height: 12px;
    width: 12px;

    svg {
      height: 100%;
      width: 100%;
    }
  `,

  SwipeIndicator: styled.div`
    display: none;

    ${({ theme }) => css`
      @media ${media(theme).coarsePointer} {
        align-items: center;
        bottom: 0;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        height: ${theme.padding.lg};
        justify-content: center;
        left: 0;
        position: absolute;
        right: 0;
      }
    `}
  `,

  SwipeChevron: styled.span`
    ${({ theme }) => css`
      border-bottom: 2px solid ${theme.colors.content.secondary};
      border-right: 2px solid ${theme.colors.content.secondary};
      height: 6px;
      margin-bottom: 3px;
      opacity: 0.6;
      transform: rotate(225deg);
      width: 6px;
    `}
  `,

  SwipeBar: styled.span`
    ${({ theme }) => css`
      background: ${theme.colors.content.secondary};
      border-radius: 2px;
      height: 4px;
      opacity: 0.35;
      width: 36px;
    `}
  `,

  VisuallyHidden: styled.span`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  `,
};
